import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { MusicNote } from '@mui/icons-material';
import api from '../../services/api';
import { eventBus } from '../../services/EventBus';
import {
  isHttpsAlbumArtUrl,
  lookupCachedTrackImage,
  resolvePreferredAlbumArt,
} from '../../services/sonosApi';

interface AlbumArtImageProps {
  speakerId: number;
  /** URL de portada en Sonos o Spotify — clave estable para cachear sin parpadeo entre temas. */
  artUrlKey?: string | null;
  /** Fallback cuando aún no hay URL de portada. */
  titleKey?: string | null;
  artistKey?: string | null;
  uriKey?: string | null;
  /** Si false, no consulta Sonos ni muestra portada cacheada (solo placeholder). */
  fetchArt?: boolean;
  size?: number | string;
  borderRadius?: number | string;
  sx?: object;
}

/** Cache en memoria por URL de portada para no re-descargar ni vaciar entre temas. */
const blobCache = new Map<string, string>();
const MAX_CACHE = 64;

function buildCacheKey(
  speakerId: number,
  artUrlKey?: string | null,
  titleKey?: string | null,
  artistKey?: string | null,
): string {
  const preferred = resolvePreferredAlbumArt({
    remoteUrl: artUrlKey,
    title: titleKey,
    artist: artistKey,
  });
  if (preferred) return preferred;
  return `speaker:${speakerId}:title:${titleKey ?? ''}|${artistKey ?? ''}`;
}

function rememberInCache(key: string, objectUrl: string): void {
  if (blobCache.has(key)) return;
  blobCache.set(key, objectUrl);
  if (blobCache.size > MAX_CACHE) {
    const oldest = blobCache.keys().next().value;
    if (oldest) {
      const stale = blobCache.get(oldest);
      blobCache.delete(oldest);
      // Solo revocar object URLs locales (blob:); las https de Spotify no se crean con createObjectURL.
      if (stale?.startsWith('blob:')) URL.revokeObjectURL(stale);
    }
  }
}

const AlbumArtImage: React.FC<AlbumArtImageProps> = ({
  speakerId,
  artUrlKey,
  titleKey,
  artistKey,
  uriKey,
  fetchArt = true,
  size = 48,
  borderRadius = 1.5,
  sx,
}) => {
  const preferredUrl = resolvePreferredAlbumArt({
    remoteUrl: artUrlKey,
    uri: uriKey,
    title: titleKey,
    artist: artistKey,
  });
  const cacheKey = buildCacheKey(speakerId, artUrlKey, titleKey, artistKey);
  const httpsDirect = isHttpsAlbumArtUrl(preferredUrl) ? preferredUrl : null;
  const cached = fetchArt ? (httpsDirect || blobCache.get(cacheKey) || null) : null;
  const [src, setSrc] = useState<string | null>(cached);
  const activeKeyRef = useRef(cacheKey);

  useEffect(() => {
    activeKeyRef.current = cacheKey;

    if (!fetchArt) {
      setSrc(null);
      return undefined;
    }

    // Sin URL ni título: cola vaciada / idle → placeholder (no conservar foto previa).
    if (!artUrlKey && !titleKey && !preferredUrl) {
      setSrc(null);
      return undefined;
    }

    // 1) Tapa local https (biblioteca / cola) — no ir afuera.
    const localHttps = isHttpsAlbumArtUrl(preferredUrl)
      ? preferredUrl
      : lookupCachedTrackImage(uriKey, titleKey, artistKey);
    if (localHttps) {
      rememberInCache(cacheKey, localHttps);
      setSrc(localHttps);
      return undefined;
    }

    const hit = blobCache.get(cacheKey);
    if (hit) {
      setSrc(hit);
      return undefined;
    }

    // 2) Último recurso: bajar vía servidor la tapa del parlante (http LAN).
    if (!Number.isFinite(speakerId) || speakerId <= 0) {
      setSrc(null);
      return undefined;
    }
    if (!artUrlKey && !titleKey) {
      setSrc(null);
      return undefined;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const res = await api.get(`/sonos/speakers/${speakerId}/album-art`, { responseType: 'blob' });
        if (cancelled || activeKeyRef.current !== cacheKey) return;
        objectUrl = URL.createObjectURL(res.data);
        rememberInCache(cacheKey, objectUrl);
        setSrc(objectUrl);
      } catch {
        if (!cancelled && activeKeyRef.current === cacheKey) {
          setSrc((prev) => (artUrlKey ? prev : null));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [speakerId, cacheKey, fetchArt, preferredUrl, artUrlKey, titleKey, artistKey, uriKey]);

  // Si más tarde llega la tapa desde la biblioteca, refrescar sin ir afuera.
  useEffect(() => {
    const onImages = () => {
      const local = lookupCachedTrackImage(uriKey, titleKey, artistKey);
      if (local) setSrc(local);
    };
    eventBus.on('sonos:queue-images-updated', onImages);
    return () => {
      eventBus.off('sonos:queue-images-updated', onImages);
    };
  }, [uriKey, titleKey, artistKey]);

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius,
        flexShrink: 0,
        backgroundColor: '#2a2a2a',
        backgroundImage: src ? `url(${src})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...sx,
      }}
    >
      {!src && <MusicNote sx={{ color: '#555', fontSize: typeof size === 'number' ? size * 0.45 : 22 }} />}
    </Box>
  );
};

export default AlbumArtImage;
